import { USER_LOGIN_COOKIE } from "../constants";
import bcrypt from "bcryptjs";
import { sendEmail } from "../emailHandler";
import { ConfirmationNumber } from "@mui/icons-material";

export const isMobile = window.innerWidth <= 600;
export const isTablet = window.innerWidth <= 899;
export const isDesktop = window.innerWidth >= 900;

export function formatDate(timestamp: number, shouldDayIncluded = true) {
  // If the timestamp is already in milliseconds, don't multiply it
  const isMilliseconds = timestamp.toString().length > 10;
  const date = isMilliseconds
    ? new Date(timestamp)
    : new Date(timestamp * 1000);

  // Array of month names
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Get the month, day, and year
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  // Return the formatted date string
  if (shouldDayIncluded) {
    return `${month} ${day}, ${year}`;
  } else {
    return `${month} ${year}`;
  }
}

/**
 * Retrieves the current URL of the webpage.
 *
 * @return {string} The current URL of the webpage.
 */
export function getCurrentUrl() {
  return window.location.href;
}

export function getSellerName(sellerEmail: string) {
  return sellerEmail.split("@")[0];
}

export function currentTimestamp() {
  return Date.now();
}

export function addDaysToCurrentTimeStamp(days: number) {
  let currentTimestamp = Date.now();

  let futureTimestamp = currentTimestamp + days * 24 * 60 * 60 * 1000;

  return formatDate(Number(String(futureTimestamp).slice(0, 10)));
}

export function createProductImageObject(
  productImageOne?: string,
  productImageTwo?: string,
  productImageThree?: string,
  productName?: string
) {
  return [
    ...(productImageOne && productImageOne !== ""
      ? [
          {
            original: productImageOne,
            thumbnail: productImageOne,
            altText: `${productName} 01`,
          },
        ]
      : []),
    ...(productImageTwo && productImageTwo !== ""
      ? [
          {
            original: productImageTwo,
            thumbnail: productImageTwo,
            altText: `${productName} 02`,
          },
        ]
      : []),
    ...(productImageThree && productImageThree !== ""
      ? [
          {
            original: productImageThree,
            thumbnail: productImageThree,
            altText: `${productName} 03`,
          },
        ]
      : []),
  ];
}

export function urlWithForwardSlash(text: string) {
  if (!text.startsWith("/")) {
    text = "/" + text;
  }
  return text;
}

export function setCookie(
  name: string,
  value: string | number,
  days = 365 * 99
): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();
  document.cookie = `${name}=${value};${expires};path=/`;
}

export function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export function getCookie(name: string): string | number | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export function differenceInDaysBetweenTwoTimeStamps(
  timestamp1: number,
  timestamp2: number
) {
  // Calculate the difference in milliseconds
  const differenceInMilliseconds = timestamp1 - timestamp2;

  // Convert milliseconds to days
  const millisecondsInADay = 24 * 60 * 60 * 1000;
  const differenceInDays = differenceInMilliseconds / millisecondsInADay;

  return differenceInDays;
}

export function setLocalStorageWithExpiry(
  key: string,
  value: any,
  expiryInDays: number
) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + expiryInDays * 24 * 60 * 60 * 1000,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

export function getLocalStorageWithExpiry(key: string): any | null {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
}

export function removeDuplicates(arr: any[], key: keyof any): any[] {
  const seen = new Set();
  return arr.filter((item) => {
    const keyValue = item[key];
    if (seen.has(keyValue)) {
      return false;
    } else {
      seen.add(keyValue);
      return true;
    }
  });
}

export function getInitialWithUpperCase(name: string): string {
  if (name.length === 0) {
    throw new Error("Input string cannot be empty.");
  }

  // Get the first character, convert to uppercase
  return name.charAt(0).toUpperCase();
}

export function getOrigin(): string {
  return window.location.origin;
}

export function isFirstOfMonth(): boolean {
  const today = new Date();
  return today.getDate() === 1;
}

export function setSession(key: string, value: string): void {
  sessionStorage.setItem(key, value);
}

export function getSession(key: string): string | null {
  return sessionStorage.getItem(key);
}

export function deleteSession(key: string): void {
  sessionStorage.removeItem(key);
}

export function clearSession(): void {
  sessionStorage.clear();
}

export function deleteLoginCookieAndSession(): void {
  deleteCookie(USER_LOGIN_COOKIE);
  deleteSession(USER_LOGIN_COOKIE);
}

export function getLoginCookieAndSession(): string | number | null {
  return getCookie(USER_LOGIN_COOKIE) || getSession(USER_LOGIN_COOKIE);
}

export async function generateHashPassword(normalPassword: string) {
  const salt = await bcrypt.genSalt(8);
  const hashPassword = await bcrypt.hash(normalPassword, salt);

  return hashPassword;
}

export function generateRandomString(length: number): string {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }
  return result;
}

export async function sendConfirmationEmail(
  email: string,
  createdTime: number
) {
  await sendEmail(
    email,
    "Confirm Your Account",
    `<div><p>confirm test</p> <a href="http://138.68.242.123/confirm-account/${createdTime}/${email}">Click here to confirm</a></div>`,
    "confirmEmail",
    {
      confirmationLink: `http://138.68.242.123/confirm-account/${createdTime}/${email}`,
    }
  );
}

export async function sendWelcomeEmail(email: string, password: string) {
  await sendEmail(
    email,
    "Welcome to FiddlePiddle",
    "welcome test",
    "userWelcome",
    {
      password: password,
    }
  );
}

export function getDifferenceInMinutesBetweenTimeStamps(
  timestamp1: number,
  timestamp2: number
): number {
  // Calculate the absolute difference in milliseconds
  const differenceInMilliseconds = Math.abs(timestamp1 - timestamp2);

  // Convert milliseconds to minutes and round to the nearest integer
  const differenceInMinutes = Math.round(
    differenceInMilliseconds / (1000 * 60)
  );

  return differenceInMinutes;
}

export function getHumanReadableTimeDifference(
  timestamp1: number,
  timestamp2: number
): string {
  const difference = Math.abs(timestamp1 - timestamp2);

  const seconds = Math.floor(difference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return years === 1 ? "1 year ago" : `${years} years ago`;
  if (months > 0) return months === 1 ? "1 month ago" : `${months} months ago`;
  if (days > 0) return days === 1 ? "1 day ago" : `${days} days ago`;
  if (hours > 0) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  if (minutes > 0)
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;
}

export function convertMinutesToHours(minutesAgo: number) {
  const hours = Math.floor(minutesAgo / 60);

  return hours;
}

export function convertTimestampToDate(timestamp: number) {
  // format the date to mm/dd/yy
  const date = new Date(timestamp * 1000);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear().toString().slice(-2);

  return `${month}/${day}/${year}`;
}

/**
 * Scrolls to a target element with smooth behavior and consistent offset
 * @param targetId - The ID of the target element to scroll to
 * @param offset - Optional offset to account for sticky headers (default: 80)
 */
export const scrollToElement = (targetId: string, offset: number = 80) => {
  const targetElement = document.getElementById(targetId);
  if (targetElement) {
    const rect = targetElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset + rect.top;

    window.scrollTo({
      top: scrollTop - offset,
      behavior: "smooth",
    });
  }
};
